/**
 * Crypto wallets affiliates are paid to (business rule 2026-10-20: crypto only). Each method is one
 * asset on one network: an address on the wrong network loses the money, so addresses and
 * transaction hashes are checked by format here, the same way in the browser and on the server.
 */

export interface CryptoNetwork {
  /** Stored in AffiliateProfile.payoutMethod / AffiliatePayout.method. */
  code: string;
  asset: 'USDT' | 'USDC' | 'BTC' | 'ETH';
  network: string;
  /** "USDT · TRC-20 (Tron)". */
  label: string;
  placeholder: string;
  /** Pegged to the dollar: the amount to send can be suggested from the USD→BRL rate. */
  dollar: boolean;
  /** Decimals the asset is sent with. */
  decimals: number;
  address: RegExp;
  hash: RegExp;
  txUrl: (hash: string) => string;
}

const EVM_ADDRESS = /^0x[0-9a-fA-F]{40}$/;
const EVM_HASH = /^0x[0-9a-fA-F]{64}$/;
const HEX64 = /^[0-9a-fA-F]{64}$/;
const TRON_ADDRESS = /^T[1-9A-HJ-NP-Za-km-z]{33}$/;
// Legacy (1…), script (3…) and bech32 / taproot (bc1…, lowercase).
const BTC_ADDRESS = /^(?:[13][1-9A-HJ-NP-Za-km-z]{25,34}|bc1[02-9ac-hj-np-z]{11,71})$/;

export const CRYPTO_NETWORKS: readonly CryptoNetwork[] = [
  { code: 'USDT_TRC20', asset: 'USDT', network: 'TRC-20', label: 'USDT · TRC-20 (Tron)', placeholder: 'Endereço Tron, começa com T', dollar: true, decimals: 6, address: TRON_ADDRESS, hash: HEX64, txUrl: (h) => `https://tronscan.org/#/transaction/${h}` },
  { code: 'USDT_BEP20', asset: 'USDT', network: 'BEP-20', label: 'USDT · BEP-20 (BNB Smart Chain)', placeholder: 'Endereço BSC, começa com 0x', dollar: true, decimals: 6, address: EVM_ADDRESS, hash: EVM_HASH, txUrl: (h) => `https://bscscan.com/tx/${h}` },
  { code: 'USDT_ERC20', asset: 'USDT', network: 'ERC-20', label: 'USDT · ERC-20 (Ethereum)', placeholder: 'Endereço Ethereum, começa com 0x', dollar: true, decimals: 6, address: EVM_ADDRESS, hash: EVM_HASH, txUrl: (h) => `https://etherscan.io/tx/${h}` },
  { code: 'USDC_ERC20', asset: 'USDC', network: 'ERC-20', label: 'USDC · ERC-20 (Ethereum)', placeholder: 'Endereço Ethereum, começa com 0x', dollar: true, decimals: 6, address: EVM_ADDRESS, hash: EVM_HASH, txUrl: (h) => `https://etherscan.io/tx/${h}` },
  { code: 'BTC', asset: 'BTC', network: 'Bitcoin', label: 'Bitcoin (BTC)', placeholder: 'Endereço Bitcoin: bc1…, 1… ou 3…', dollar: false, decimals: 8, address: BTC_ADDRESS, hash: HEX64, txUrl: (h) => `https://mempool.space/tx/${h}` },
  { code: 'ETH', asset: 'ETH', network: 'Ethereum', label: 'Ethereum (ETH)', placeholder: 'Endereço Ethereum, começa com 0x', dollar: false, decimals: 8, address: EVM_ADDRESS, hash: EVM_HASH, txUrl: (h) => `https://etherscan.io/tx/${h}` },
];

/** Methods saved before crypto-only payments (still shown on old closings and receipts). */
export const LEGACY_PAYOUT_METHODS: Readonly<Record<string, string>> = {
  PIX: 'PIX',
  DERIV: 'Transferência pela corretora',
  USDT: 'USDT (TRC-20)',
  OUTRO: 'Outro',
};

export function cryptoNetwork(code: string | null | undefined): CryptoNetwork | null {
  return CRYPTO_NETWORKS.find((n) => n.code === code) ?? null;
}

/** Readable name of any payout method, current or legacy. */
export function payoutMethodLabel(code: string | null | undefined): string {
  if (!code) return '—';
  return cryptoNetwork(code)?.label ?? LEGACY_PAYOUT_METHODS[code] ?? code;
}

/** The address, trimmed, if it has the network's format; null otherwise. */
export function normalizeWallet(code: string, address: string): string | null {
  const net = cryptoNetwork(code);
  const v = address.trim();
  return net && net.address.test(v) ? v : null;
}

/**
 * The transaction hash, trimmed, if it has the network's format; null otherwise. Explorers show the
 * same hash with or without "0x": both are accepted and stored in the network's own form.
 */
export function normalizeTxHash(code: string, hash: string): string | null {
  const net = cryptoNetwork(code);
  if (!net) return null;
  let v = hash.trim();
  if (net.hash === EVM_HASH && HEX64.test(v)) v = `0x${v}`;
  if (net.hash === HEX64 && EVM_HASH.test(v)) v = v.slice(2);
  return net.hash.test(v) ? v : null;
}

/**
 * The quantity sent ("14,80" or "14.80") as "14.80", if positive and within the asset's decimals;
 * null otherwise (never cut silently: the receipt must show exactly what was sent).
 */
export function parseCryptoAmount(code: string, text: string): string | null {
  const net = cryptoNetwork(code);
  const m = /^(\d{1,12})(?:[.,](\d+))?$/.exec(text.trim());
  if (!net || !m || (m[2]?.length ?? 0) > net.decimals) return null;
  const v = m[2] ? `${m[1]}.${m[2]}` : m[1]!;
  return Number(v) > 0 ? v : null;
}

/** "T9yD…4xAb": enough to recognise a wallet without exposing it whole. */
export function shortWallet(address: string): string {
  return address.length > 14 ? `${address.slice(0, 6)}…${address.slice(-4)}` : address;
}
